import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { DragDropContext } from 'react-beautiful-dnd';

import './emails.css';
import getEmailTemplatesList from '../../../api/graphql/queries/getEmailTemplatesList';
import getEmailTemplatePreview from '../../../api/graphql/queries/getEmailTemplatePreview';
import getEmailScopePlates from '../../../api/graphql/queries/getEmailScopePlates';
import getEmailTemplateCells from '../../../api/graphql/queries/getEmailTemplateCells';
import editEmailTemplate from '../../../api/graphql/mutations/editEmailTemplate';
import editEmailTemplateCell from '../../../api/graphql/mutations/editEmailTemplateCell';
import { draftFromTemplate, isDirty, validateDraft, extractVariables } from '../../../utils/emailDraft';
import { renderBlocks, validateDoc, createBlock, insertBlock } from '../../../utils/emailBlocks';
import { substituteSampleData } from '../../../utils/emailSampleRender';
import { starterTemplateId } from '../../../utils/emailCampaignKey';
import { htmlToBlocks } from '../../../utils/emailHtmlToBlocks';
import {
  docFromTemplate, docToJson, docIsDirty, newDoc, withSettings, withBlockProps, withBlocks,
  applyDragEnd, typeFromDraggableId,
} from '../../../utils/emailBlockDoc';
import {
  buildPlateStrip, platesNeedingReview,
  PLATE_STRIP_LOADING, PLATE_STRIP_READY, PLATE_STRIP_FAILED,
} from '../../../utils/emailPlates';
import {
  buildCellPanels, CELLS_LOADING, CELLS_READY, CELLS_FAILED,
} from '../../../utils/emailCells';
import formatTimestamp from '../../../utils/formatTimestamp';
import languages from '../../../utils/languages';
import HtmlSourceEditor from './HtmlSourceEditor';
import EmailCellEditor from './EmailCellEditor';
import { RENDER_HTML_EDITORS } from './htmlEditorSurfaces';
import BlockCanvas from './BlockCanvas';
import BlockPalette from './BlockPalette';
import BlockPropertiesPanel from './BlockPropertiesPanel';
import EmailProofPane from './EmailProofPane';
import EmailRevisionHistory from './EmailRevisionHistory';
import EmailTestSendModal from './EmailTestSendModal';
import EmailCloneModal from './EmailCloneModal';
import Modal from '../../Modal/Modal';
import Notification, { useNotification } from '../../Notification';
import LoadIcon from '../../LoadIcon';

// The press check — screen 3 of the approved design, and the one that matters
// most: edit left, proof right, always both.
//
// This screen edits exactly one (scope, language) plate. The plate switcher in
// the toolbar navigates between the scope's other plates; it does not edit them.
//
// What the design draws in the left column is a Header / Content / Footer split
// with a rich-text editor on the middle panel. That was abandoned on
// 2026-09-01: all 78 live templates keep their copy inside <td> table cells, so
// no three-region split can hold. The chrome here is the design's — toolbar,
// plate switcher, needs-review banner, the two-column .press grid, the panel
// stack, the proof pane — and the panel stack is now filled by EmailCellEditor,
// one panel per editable <td>, in the design's own panel language.
//
// The whole-document HTML editor did not go anywhere. It is the only way to
// edit the table chrome, and the only way to edit the eleven templates whose
// copy the cell finder cannot see, so it stays on this screen at every coverage
// — folded away behind an "Edit HTML" button when the cells cover the email,
// open from the start when they do not. buildCellPanels (src/utils/emailCells.js)
// makes that call from the server's measured cellCoverage.
//
// `validateDraft` (src/utils/emailDraft.js) needs to know which variables this
// scope's real sender actually supplies (spec decision 15's variableManifest),
// which is server-only and hand-maintained on purpose — duplicating it here
// would create a second copy to drift out of sync. previewEmailTemplate's
// `providedVariables` field (computed server-side from the same providedFor()
// the save-path validation uses) is that manifest's one client-visible read, so
// this screen fetches it once per template load and passes it straight into
// validateDraft. See the `providedVariablesLoaded` handling below for what
// happens before that fetch resolves.
const EmailTemplateEditor = ({ match }) => {
  const { scope, language } = match.params;

  const [template, setTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [draft, setDraft] = useState(null);
  const [originalDraft, setOriginalDraft] = useState(null);

  const [providedVariables, setProvidedVariables] = useState([]);
  const [providedVariablesLoaded, setProvidedVariablesLoaded] = useState(false);
  const [preview, setPreview] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [scopePlates, setScopePlates] = useState([]);
  const [platesStatus, setPlatesStatus] = useState(PLATE_STRIP_LOADING);
  const [platesReloadToken, setPlatesReloadToken] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [attemptedSave, setAttemptedSave] = useState(false);
  // True when a reload of this plate did not land — see loadTemplate below.
  // While it is set, what is on screen is NOT what the server holds.
  const [reloadFailed, setReloadFailed] = useState(false);

  // Monotonic token identifying the newest wanted view of this plate. Bumped
  // by the plate-load effect below (every scope/language change) and by every
  // imperative loadTemplate() call. A response is applied only while the token
  // it captured is still the current one — the imperative equivalent of the
  // `ignore` flag the effects use, which loadTemplate cannot have because it
  // is called from an event handler and has no cleanup to run.
  const plateToken = useRef(0);

  // The block document, when this template has one. null means "not
  // block-authored", which is every one of the 78 hand-written live templates
  // and stays that way until somebody converts one. THE SWITCH IS ON THIS
  // VALUE, never on a flag: docFromTemplate(template) is what decides which
  // editor the screen shows.
  const [blockDoc, setBlockDoc] = useState(null);
  const [originalBlockDoc, setOriginalBlockDoc] = useState(null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  // The debounced render behind the proof pane. The canvas is never debounced.
  const [blocksProof, setBlocksProof] = useState('');
  // The result of the last "Convert to blocks", or null. It is a REVIEW, not a
  // state of the template: nothing is written while it is set, and it is thrown
  // away on save, on abandon and on leaving the plate. Its presence is what puts
  // the proof pane into its side-by-side mode and what makes the screen show the
  // list of what did not convert.
  const [conversion, setConversion] = useState(null);

  const [cells, setCells] = useState([]);
  const [cellCoverage, setCellCoverage] = useState(null);
  const [cellsStatus, setCellsStatus] = useState(CELLS_LOADING);
  const [cellDirty, setCellDirty] = useState(false);
  const [isHtmlOpen, setIsHtmlOpen] = useState(false);

  // Only used by the notFound branch below, where this plate does not exist yet
  // and the screen offers to write it.
  const [newPlateSubject, setNewPlateSubject] = useState('');
  const [isCreatingPlate, setIsCreatingPlate] = useState(false);

  const [isRevisionsOpen, setIsRevisionsOpen] = useState(false);
  const [isTestSendModalOn, setIsTestSendModalOn] = useState(false);
  const [isCloneModalOn, setIsCloneModalOn] = useState(false);

  const [status, handleStatus] = useNotification();

  useEffect(() => {
    // Single route reused across every plate — navigating between edit URLs
    // (browser back/forward, a bookmarked link) swaps match.params on the same
    // mounted instance rather than remounting it. Same guard as
    // EmailTemplateDetail.jsx: `ignore` is scoped to this effect run, so a late
    // response for a previous scope/language can never overwrite a newer one.
    let ignore = false;
    // Any in-flight loadTemplate() belongs to the plate we are leaving.
    plateToken.current += 1;

    setIsLoading(true);
    setNotFound(false);
    setReloadFailed(false);
    setTemplate(null);
    setDraft(null);
    setOriginalDraft(null);
    setProvidedVariables([]);
    setProvidedVariablesLoaded(false);
    setPreview(null);
    setPreviewError(null);
    setAttemptedSave(false);
    setIsRevisionsOpen(false);
    // The cells belong to the plate that was on screen, not to this one. The
    // fetch below is keyed on `template`, which is null until the load
    // resolves, so without this reset the new plate would briefly render the
    // old plate's copy — and offer to save it.
    setCells([]);
    setBlockDoc(null);
    setOriginalBlockDoc(null);
    setSelectedBlockId(null);
    setBlocksProof('');
    setConversion(null);
    setCellCoverage(null);
    setCellsStatus(CELLS_LOADING);
    setCellDirty(false);
    setIsHtmlOpen(false);

    console.log('Calling getEmailTemplatesList()');
    getEmailTemplatesList({ scope, language }).then((templatesDataObject) => {
      if (ignore) {
        return;
      }
      if (templatesDataObject) {
        if (templatesDataObject.isAuth) {
          const [found] = templatesDataObject.templates || [];
          if (found) {
            setTemplate(found);
            const builtDraft = draftFromTemplate(found);
            setDraft(builtDraft);
            setOriginalDraft(builtDraft);
            const openedDoc = docFromTemplate(found);
            setBlockDoc(openedDoc);
            setOriginalBlockDoc(openedDoc);
          } else {
            // A missing template for this scope/language is reachable via a
            // direct or bookmarked URL — nothing to edit, not an error.
            setNotFound(true);
          }
          setIsLoading(false);
        } else {
          // isAuth convention used across the Emails tab: log and leave
          // isLoading true rather than rendering a form with no data behind it.
          console.log('getEmailTemplatesList failed!');
        }
      } else {
        console.log('templatesData = null');
      }
    });

    return () => {
      ignore = true;
    };
  }, [scope, language]);

  useEffect(() => {
    // The plate switcher and the needs-review banner. Metadata only — see
    // getEmailScopePlates.js for why this is not the full template query. Keyed
    // on `scope` alone: switching language inside the same campaign does not
    // change which plates exist. Same staleness guard as every other fetch here.
    let ignore = false;

    setScopePlates([]);
    setPlatesStatus(PLATE_STRIP_LOADING);

    console.log('Calling getEmailScopePlates()');
    getEmailScopePlates({ scope }).then((platesDataObject) => {
      if (ignore) {
        return;
      }
      if (platesDataObject) {
        if (platesDataObject.isAuth) {
          setScopePlates(platesDataObject.templates || []);
          setPlatesStatus(PLATE_STRIP_READY);
        } else {
          // Deliberately NOT the log-and-stay-loading convention the rest of
          // this tab uses for isAuth: false. An unanswered plate fetch that
          // leaves the strip looking empty is the failure this state exists
          // for - four empty squares are a claim that those plates do not
          // exist, and one of them may be a needsReview plate about to send
          // stale copy. Say it broke instead.
          console.log('getEmailScopePlates failed!');
          setPlatesStatus(PLATE_STRIP_FAILED);
        }
      } else {
        console.log('platesData = null');
        setPlatesStatus(PLATE_STRIP_FAILED);
      }
    });

    return () => {
      ignore = true;
    };
  }, [scope, platesReloadToken]);

  useEffect(() => {
    if (!template) {
      // Reachable right after the effect above resets `template` to null on a
      // scope/language change — must not fire a preview fetch with an
      // undefined templateId.
      return undefined;
    }

    let ignore = false;

    setIsPreviewLoading(true);
    setPreviewError(null);

    console.log('Calling getEmailTemplatePreview()');
    getEmailTemplatePreview({ templateId: template.templateId }).then((previewDataObject) => {
      if (ignore) {
        return;
      }
      if (previewDataObject) {
        if (previewDataObject.isAuth) {
          const { preview: rendered } = previewDataObject;
          if (rendered) {
            setProvidedVariables(rendered.providedVariables || []);
            setProvidedVariablesLoaded(true);
            setPreview(rendered);
          } else {
            setPreviewError(previewDataObject.error || 'Preview failed to render.');
          }
          // A preview that fails to render (isAuth true, preview falsy) leaves
          // providedVariablesLoaded false — see the validation derivation below
          // for why that is the safe state to stay in, not a bug to fix here.
          setIsPreviewLoading(false);
        } else {
          console.log('getEmailTemplatePreview failed!');
        }
      } else {
        console.log('previewData = null');
      }
    });

    return () => {
      ignore = true;
    };
  }, [template]);

  useEffect(() => {
    // The editable cells of this plate. Keyed on `template` — the same object
    // every save replaces — which is what makes "re-read the cells after every
    // successful save" fall out of the normal flow rather than being a step
    // someone has to remember. It has to happen: cell ids are ordinals and the
    // server splices at offsets it recomputes from the stored html, so after an
    // edit that changes content length a stale list points at the wrong bytes.
    // (The server refuses a moved cell rather than splicing it, so the worst a
    // stale list can do is fail — but failing a legitimate edit is still bad.)
    if (!template) {
      return undefined;
    }

    let ignore = false;

    setCellsStatus(CELLS_LOADING);

    console.log('Calling getEmailTemplateCells()');
    getEmailTemplateCells({ scope, language }).then((cellsDataObject) => {
      if (ignore) {
        return;
      }
      if (cellsDataObject && cellsDataObject.isAuth) {
        const [found] = cellsDataObject.templates || [];
        // A response for a different plate is not this plate's copy. Offering
        // it would be offering to save another template's words into this one.
        if (found && found.templateId === template.templateId) {
          setCells(found.editableCells || []);
          setCellCoverage(typeof found.cellCoverage === 'number' ? found.cellCoverage : null);
          setCellsStatus(CELLS_READY);
        } else {
          setCellsStatus(CELLS_FAILED);
        }
      } else {
        // Same reasoning as the plate strip, not the log-and-stay-loading
        // convention: an empty cell list is a claim that this email has no
        // editable copy, and eleven templates for which that IS true make the
        // claim believable. Say the read failed instead.
        console.log('getEmailTemplateCells failed!');
        setCellsStatus(CELLS_FAILED);
      }
    });

    return () => {
      ignore = true;
    };
  }, [template, scope, language]);

  useEffect(() => {
    // Registered only while there is something to lose, and always removed on
    // cleanup — an effect that ran once and never cleaned up its listener would
    // leave it attached (and blocking navigation) on every other screen in the
    // app for the rest of the session.
    // cellDirty is an open cell editor with typed-in changes. It is unsaved
    // work exactly like a modified draft, and it lives inside EmailCellEditor,
    // which cannot register this listener itself without every panel fighting
    // over one window handler.
    // An unsaved block document is unsaved work exactly like a modified draft:
    // nothing on the canvas has been written anywhere until Save plate runs.
    const dirty = cellDirty
      || (draft && originalDraft && isDirty(draft, originalDraft))
      || docIsDirty(blockDoc, originalBlockDoc);
    if (!dirty) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      // Chrome requires returnValue to be set for the native prompt to appear.
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [draft, originalDraft, cellDirty, blockDoc, originalBlockDoc]);

  // The HTML this document would be saved as, recomputed on every edit. It is
  // what the proof shows, what the variable check reads and what the save
  // sends, so there is exactly one render and no way for the three to disagree.
  // renderBlocks is pure string building; the expensive part is the iframe, not
  // this.
  const blocksHtml = useMemo(() => (blockDoc ? renderBlocks(blockDoc) : ''), [blockDoc]);

  // The same render, with sample data substituted — and ONLY for the proof.
  //
  // Off the block path the proof is previewEmailTemplate's server render, which
  // substitutes `{ ...baselineSampleData(used), ...sampleData }` before it
  // renders. The builder renders locally and never calls that resolver, so its
  // proof showed `{{firstName}}` literally while the cell editor on this very
  // screen showed a substituted value. substituteSampleData closes that gap
  // over the same data with the same precedence (src/utils/emailSampleRender.js).
  //
  // `blocksHtml` above stays un-substituted, because it is what the save sends
  // and what the variable check reads. Substituting there would write
  // `VAR_firstName` into a sent email.
  //
  // `draft` is still null while the plate loads — this hook runs above the
  // early return that renders the spinner, so it must not assume one.
  const sampleDataJson = draft ? draft.sampleDataJson : '';
  const blocksProofHtml = useMemo(
    () => substituteSampleData(blocksHtml, sampleDataJson),
    [blocksHtml, sampleDataJson],
  );

  useEffect(() => {
    // The PROOF is debounced; the canvas never is. Re-parsing a whole HTML
    // document into a sandboxed iframe on every keystroke is what would make
    // typing feel broken — but the canvas is a list of rows, costs nothing to
    // redraw, and is the feedback loop the user is actually looking at while
    // they type.
    if (!blockDoc) {
      setBlocksProof('');
      return undefined;
    }
    const timer = setTimeout(() => setBlocksProof(blocksProofHtml), 180);
    return () => clearTimeout(timer);
  }, [blocksProofHtml, blockDoc]);

  // Re-fetches this same (scope, language) plate and replaces `template`,
  // `draft` and `originalDraft` with what the server now has. Used after a
  // revision restore (Task 8) so the editor shows the restored content
  // instead of a stale pre-restore draft - getting this wrong means the
  // screen lies about what is actually saved. Deliberately does not toggle
  // `isLoading`: this component renders nothing but a spinner while that is
  // true, which would also blank out the "Version restored." notification
  // set right after this resolves.
  const loadTemplate = async () => {
    // Staleness guard. The effects in this file use a closure-scoped `ignore`
    // set by their own cleanup; an imperative call has no cleanup, so it uses
    // the shared `plateToken` ref instead. Claiming the token here supersedes
    // any earlier reload, and the plate-load effect claims it on every
    // scope/language change - so a response is applied only if nothing newer
    // has been asked for since. Without it, restoring on EN and then clicking
    // ES lands the EN document in `template`/`draft` while the URL and the
    // plate chip say ES, and the next Save plate writes Spanish copy to the
    // English plate.
    plateToken.current += 1;
    const requestToken = plateToken.current;

    const templatesDataObject = await getEmailTemplatesList({ scope, language });

    if (plateToken.current !== requestToken) {
      // A newer plate (or a newer reload of this one) is what the screen is
      // showing. Applying this response would be the bug above.
      return false;
    }

    if (templatesDataObject && templatesDataObject.isAuth) {
      const [found] = templatesDataObject.templates || [];
      if (found) {
        setTemplate(found);
        const builtDraft = draftFromTemplate(found);
        setDraft(builtDraft);
        setOriginalDraft(builtDraft);
        const openedDoc = docFromTemplate(found);
        setBlockDoc(openedDoc);
        setOriginalBlockDoc(openedDoc);
        setSelectedBlockId(null);
        setAttemptedSave(false);
        setReloadFailed(false);
        return true;
      }
    }

    // Everything else - isAuth false, a null response, or no document for this
    // plate - means the editor is still holding the PRE-reload draft while the
    // caller (a revision restore) has already told the user it succeeded. The
    // log-and-say-nothing convention cannot apply here: `dirty` reads false, so
    // the toolbar would claim this plate is saved and Save plate would write
    // the stale body back over the restore. Say it out loud instead, and stop
    // the whole-document save until a reload lands.
    console.log('loadTemplate failed!');
    setReloadFailed(true);
    return false;
  };

  // Writes the first plate of a campaign that has none. See the notFound branch
  // below for why this screen is where that happens.
  //
  // isActive:false is deliberate and is NOT the campaign flag: EmailTemplate's
  // isActive is the one routing actually reads (resolveTemplateForScopes.js), so
  // a plate created with nothing in it must not be reachable by a send. The
  // editor's own Active control turns it on once it has been written.
  const handleCreatePlate = async () => {
    const subject = newPlateSubject.trim();
    if (!subject || isCreatingPlate) return;

    setIsCreatingPlate(true);
    const doc = newDoc();
    const result = await editEmailTemplate({
      // Same scheme as cloneTemplate() in ps-back/util/email/cloneCampaign.js. A
      // second scheme here would produce plates the clone path could never match.
      templateId: starterTemplateId(scope, language),
      // The label starts out as the id, which is what 80 of the 92 templates in
      // staging do. It used to be `<scope> · <language>`, a third shape that
      // matched neither those nor the human-written labels.
      name: starterTemplateId(scope, language),
      language,
      scope,
      subject,
      htmlContent: renderBlocks(doc),
      blocksJson: JSON.stringify(doc),
      isActive: false,
    });
    setIsCreatingPlate(false);

    if (!result) {
      handleStatus('icon-close-solid', 'Could not reach the server — check your connection and try again.', 'red');
      return;
    }
    if (!result.isAuth) {
      handleStatus('icon-close-solid', 'You are not authorized to create templates.', 'red');
      return;
    }
    if (!result.success) {
      handleStatus('icon-close-solid', result.error || 'Could not create the plate.', 'red');
      return;
    }

    // Load FIRST, then leave the empty state — never the other way round.
    //
    // Clearing notFound before the plate is in state renders the main editor with
    // draft still null, and the very first thing it derives is isDirty(draft, ...),
    // which reads draft.subject and throws. The screen goes blank. loadTemplate
    // fills template/draft/blockDoc together, so by the time notFound clears there
    // is a document behind the editor.
    const loaded = await loadTemplate();
    if (!loaded) {
      handleStatus('icon-close-solid', 'The plate was created but could not be opened — reload the page.', 'red');
      return;
    }
    setNotFound(false);
  };

  // Same toggle pattern as ProductsContainer.jsx's handleClick(action, item):
  // Modal.jsx calls handleClick(type) on Escape/backdrop/close-button, so the
  // `type` passed to each <Modal> below must match a case here.
  const handleModalClick = (type) => {
    switch (type) {
      case 'testSend':
        setIsTestSendModalOn(previous => !previous);
        break;
      case 'clone':
        setIsCloneModalOn(previous => !previous);
        break;
      default:
        console.log('wrong modal type!');
        break;
    }
  };

  /*
  A plate opens in the builder. Always, without being asked.

  Blocks are the only way this tab edits an email now, so a plate that arrives
  without a block document is converted on load rather than being offered a
  choice nobody wants to make. The old panel asked "build with blocks?" as though
  the HTML editors were still an option; they are not — they are behind
  RENDER_HTML_EDITORS and off.

  This still WRITES NOTHING. htmlToBlocks reads draft.htmlContent and returns a
  document; the stored email is replaced only by Save plate, and the version
  before it stays in Revisions. Converting on open costs nothing to back out of.

  The refusal path is unchanged and still matters: if the converter cannot place
  something, it says what and opens no builder, because a partial conversion
  shown in an editor is a partial conversion someone will save. That plate is
  then not editable here — which is the true state of it, not a failure to hide.

  Guarded on `conversion` so a refusal is not retried on every render, and on
  `blockDoc` so it never runs for a plate that already has one.
  */
  useEffect(() => {
    if (!template || !draft) return;
    if (blockDoc || conversion) return;
    if (docFromTemplate(template)) return;

    const result = htmlToBlocks(draft.htmlContent);
    setConversion(result);
    if (result.ok) {
      setBlockDoc(result.doc);
      setOriginalBlockDoc(result.doc);
    }
  }, [template, draft, blockDoc, conversion]);

  // Backs out of a document that was never saved, returning to the cell and
  // HTML editors. Only offered while nothing is stored — a template that really
  // is block-authored is not something to abandon by accident.

  if (isLoading) {
    return (
      <div className="emails-app">
        <div className="emails-loading">
          <LoadIcon />
          Loading template…
        </div>
      </div>
    );
  }

  // A missing plate is where a plate gets WRITTEN, not a dead end. Until this
  // existed, a campaign with no templates could not get its first one from
  // anywhere in the admin: cloning needs a source that already has plates, and
  // this screen — the only other way in — said "No template exists" and offered
  // a link back to the list.
  //
  // There is no create mutation behind this. editEmailTemplate upserts, so a
  // first plate is an ordinary save of an empty block document.
  if (notFound) {
    return (
      <div className="emails-app">
        <div className="toolbar centered">
          <div className="titleblock">
            <Link to="/cms/emails" className="btn quiet">
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M8.5 2L3.5 7l5 5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              All campaigns
            </Link>
            <div>
              <div className="tname">{`New ${language.toUpperCase()} plate`}</div>
              <div className="scope-key tkey">{scope}</div>
            </div>
          </div>
        </div>

        <div className="panel newplate">
          <div className="head">
            <h4>Write this plate</h4>
          </div>
          <div className="body">
            {status.text && <Notification {...status} />}

            <p className="hint">
              {`There is no ${language.toUpperCase()} email for `}
              <b>{scope}</b>
              {' yet. Creating one opens the block builder on an empty email.'}
            </p>

            <div className="field">
              <label htmlFor="new-plate-subject">
                Subject
                <input
                  id="new-plate-subject"
                  type="text"
                  className="textinput"
                  value={newPlateSubject}
                  onChange={event => setNewPlateSubject(event.target.value)}
                  placeholder="Confirm your address"
                  autoComplete="off"
                />
              </label>
              <p className="hint">
                Required — a plate with no subject is refused on save, so it is asked for here
                rather than after the work is done.
              </p>
            </div>

            <p className="hint">
              The plate is created switched off, so it cannot send while it is still empty. Turn
              it on with the Active control once it says what you want it to say.
            </p>

            <div className="mactions">
              <button
                type="button"
                className="btn"
                onClick={handleCreatePlate}
                disabled={isCreatingPlate || !newPlateSubject.trim()}
              >
                {isCreatingPlate ? 'Creating…' : 'Create plate'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // documentDirty drives what "Save plate" would write, and gates cell editing
  // (a cell edit is spliced into the SAVED body server-side, so an unsaved
  // draft and a cell edit cannot both be pending). `dirty` is the broader
  // question — is there unsaved work of any kind — and is what the toolbar
  // chip and the navigation guards ask.
  // The builder is on whenever there is a document to draw, which is now always:
  // a plate either arrives with blocks or is converted into them when it opens.
  const builderOn = Boolean(blockDoc);

  // THE DRAG, and why it lives up here rather than inside the canvas.
  //
  // The palette is in the left column and the email is the right-hand pane, so
  // a tile dragged from one to the other crosses the `.press` grid. One
  // DragDropContext has to wrap both, and this is the nearest thing that does.
  //
  // Nothing else about it changed: `isDropDisabled` source Droppables in
  // BlockPalette, one destination Droppable in BlockCanvas, string draggableIds
  // (createBlock's ids already are), and the whole switch on source.droppableId
  // inside applyDragEnd (src/utils/emailBlockDoc.js) where it can be tested.
  // react-beautiful-dnd 13 does not survive React 18 StrictMode; src/browser/
  // index.jsx deliberately does not mount it.
  const commitBlocks = (nextBlocks, nextSelectedId) => {
    setBlockDoc(previous => withBlocks(previous, nextBlocks));
    if (nextSelectedId !== undefined) setSelectedBlockId(nextSelectedId);
  };

  const handleBlockDragEnd = (result) => {
    if (!blockDoc) return;
    const next = applyDragEnd(blockDoc.blocks, result);
    // A tile dragged out of the palette becomes the selected block, so its
    // properties are already showing when it lands.
    const isNew = result.destination && typeFromDraggableId(result.draggableId);
    const landed = isNew && next[result.destination.index];
    commitBlocks(next, landed ? landed.id : undefined);
  };

  // The palette's other gesture: click a tile to append. It is what people do
  // when the email is empty and there is nowhere to drop yet.
  const handleAddBlock = (type) => {
    if (!blockDoc) return;
    const block = createBlock(type);
    commitBlocks(insertBlock(blockDoc.blocks, block), block.id);
  };

  // A conversion that succeeded and is on screen, not yet saved. It is the only
  // state that puts two emails side by side, and it ends the moment the plate is
  // saved, abandoned or left.
  // A conversion that was refused. There is no builder for this one — only the
  // list of what could not be placed.
  const declinedConversion = Boolean(conversion && !conversion.ok);
  const blocksDirty = docIsDirty(blockDoc, originalBlockDoc);
  // In the builder, the html that will be saved is the one generated from the
  // blocks — not draft.htmlContent, which is still the stored document until
  // the first save. Validation, the variable chips and the save all read this
  // one, so what is checked is what goes out.
  const effectiveDraft = builderOn ? { ...draft, htmlContent: blocksHtml } : draft;
  const documentDirty = isDirty(draft, originalDraft) || blocksDirty;
  const dirty = documentDirty || cellDirty;
  const rawValidation = validateDraft(effectiveDraft, providedVariables);
  // Until providedVariablesLoaded is true, `providedVariables` is still `[]`
  // (its initial/reset value), which would make validateDraft flag every
  // variable the draft uses as UNPROVIDED_VARIABLE - including one the scope
  // genuinely does supply but the last-saved content never referenced (e.g.
  // adding {{{confirmationLink}}} to a plate that never used it before). That
  // false positive would block a legitimate save with no way to proceed. So
  // UNPROVIDED_VARIABLE errors are suppressed while the real list is still
  // unknown; BLANK_SUBJECT and INVALID_SAMPLE_DATA_JSON are unrelated to that
  // fetch and stay enforced immediately either way. The server always
  // re-checks on save regardless of what the client suppressed.
  const draftValidation = providedVariablesLoaded
    ? rawValidation
    : {
      valid: rawValidation.errors.every(error => error.code === 'UNPROVIDED_VARIABLE'),
      errors: rawValidation.errors.filter(error => error.code !== 'UNPROVIDED_VARIABLE'),
    };

  // The block document's own checks, on top of the draft's: a button with no
  // href, an image with no src, an unknown type. validateDoc names the block,
  // so the message can point at one rather than saying "something is wrong".
  const blockErrors = [];
  if (builderOn) {
    validateDoc(blockDoc).errors.forEach((message) => {
      blockErrors.push({ code: 'INVALID_BLOCK', message });
    });
    if (blockDoc.blocks.length === 0) {
      // Saving now would render an empty document over the stored HTML. That is
      // never what someone opening the builder to look around intends.
      blockErrors.push({
        code: 'EMPTY_BLOCK_DOC',
        message: 'Add at least one block before saving — an empty document would replace this email with a blank one.',
      });
    }
  }

  const validation = {
    valid: draftValidation.valid && blockErrors.length === 0,
    errors: draftValidation.errors.concat(blockErrors),
  };

  const setField = (field, value) => {
    setDraft(previousDraft => ({ ...previousDraft, [field]: value }));
  };

  // Opens an empty document on a template that has none. It does NOT touch the
  // stored html: draft.htmlContent is left exactly as loaded and only the first
  // Save plate replaces it, so opening the builder to look around costs
  // nothing. `originalBlockDoc` is the same empty document, so the screen is
  // not dirty until a block is actually added.
  /*
  `handleStartFromBlocks` and `handleConvertToBlocks` used to live here, behind the
  two buttons of a "Build with blocks — optional" panel. Both are gone: blocks are
  no longer optional, so a plate is converted when it opens rather than when
  somebody presses a button, and an empty document is what a brand-new plate gets
  from the create flow. The conversion itself is unchanged — see the effect below,
  which calls the same htmlToBlocks and honours the same refusal.

  `handleAbandonBlocks` went the same way. It threw a converted document away and
  returned to the HTML editors, which no longer exist to return to.
  */

  const handleBackClick = (event) => {
    // eslint-disable-next-line no-alert
    if (dirty && !window.confirm('Discard unsaved changes and leave this page?')) {
      event.preventDefault();
    }
  };

  const handleSave = async () => {
    setAttemptedSave(true);

    if (!validation.valid) {
      handleStatus('icon-close-solid', 'Fix the errors below before saving.', 'red');
      return;
    }

    setIsSaving(true);

    const result = await editEmailTemplate({
      id: template._id,
      templateId: template.templateId,
      // Echoed, deliberately. This briefly derived the name instead, to heal
      // plates cloned before cloneCampaign stopped copying it - but `name` is a
      // label, not an id, and six templates in staging carry human text in it
      // ("Google Sheets Descompte Residents BCN 2025"). Deriving would have
      // overwritten every one of them the next time anyone pressed Save.
      // Saving an email is not the place to rename it.
      name: template.name,
      language: template.language,
      scope: template.scope,
      subject: draft.subject,
      preheader: draft.preheader,
      // In the builder the blocks ARE the document, and the html is generated
      // from them here, in the same action that sends them. The server never
      // renders blocks: the serialiser is a frontend module and a second
      // implementation there would drift out of step with this one.
      htmlContent: builderOn ? blocksHtml : draft.htmlContent,
      // undefined, not null, off the block path: the mutation reads an absent
      // blocksJson as "this save says nothing about blocks" and leaves whatever
      // is stored alone.
      blocksJson: builderOn ? docToJson(blockDoc) : undefined,
      textContent: draft.textContent,
      sampleDataJson: draft.sampleDataJson,
      // SAVING IS THE REVIEW, so the flag clears here.
      //
      // This sent `template.needsReview` straight back, which meant a plate
      // could never stop needing review: a clone stamps every copied plate
      // needsReview, and the one act that proves someone has looked at it — a
      // person opening it and pressing Save — faithfully wrote the flag back
      // unchanged. The banner and the amber pips stayed up forever.
      //
      // False unconditionally, not `template.needsReview && !dirty` or similar:
      // the flag means "copied word for word and not looked at since", and it
      // has been looked at. Nothing else in the app sets it, and a clone sets it
      // again for the plates it creates.
      needsReview: false,
      isActive: template.isActive,
      appName: template.appName,
    });

    setIsSaving(false);

    if (result && result.isAuth && result.success && result.template) {
      setTemplate(result.template);
      const savedDraft = draftFromTemplate(result.template);
      setDraft(savedDraft);
      setOriginalDraft(savedDraft);
      // What came back is now the stored document, so it is also the baseline
      // the dirty check compares against. Falling back to the doc we just sent
      // keeps the builder open if the server echoed a template without blocks.
      const savedDoc = docFromTemplate(result.template) || blockDoc;
      setBlockDoc(savedDoc);
      setOriginalBlockDoc(savedDoc);
      setAttemptedSave(false);
      // The review is over: this IS the template now, and the HTML it replaced
      // went into the revision history on the way, like any other edit.
      setConversion(null);
      // The strip holds its own copy of every plate's needsReview, fetched once
      // per scope. This save just cleared one of them, so ask again — otherwise
      // the pip on the plate you are looking at stays amber until a reload.
      setPlatesReloadToken(previous => previous + 1);
      handleStatus('icon-checkbox', 'Template saved.', 'green');
    } else if (result && result.isAuth) {
      // The server's own error string names the specific blocked variable or
      // rejection reason (validation.js's UNPROVIDED_VARIABLE/BLANK_SUBJECT
      // messages, or the sampleDataJson parse error) — surface it verbatim
      // rather than a generic message.
      handleStatus('icon-close-solid', result.error || 'Save failed.', 'red');
    } else if (result) {
      handleStatus('icon-close-solid', 'You are not authorized to save this template.', 'red');
    } else {
      // editEmailTemplate() never throws — a null result here means a network
      // failure. PostContainer's save flow does nothing visible in this case;
      // this screen must not repeat that for hand-written, unrecoverable HTML.
      handleStatus('icon-close-solid', 'Save failed — check your connection and try again.', 'red');
    }
  };

  // One cell, through its own mutation — never the whole-document save path.
  // The client sends the cell's id and its new contents and nothing else: the
  // server splices at the offsets it recomputes from the html it is holding,
  // so no composed body from here can smear an edit across the table chrome.
  //
  // The result's template is the whole post-save document, and it replaces
  // `template`, `draft` and `originalDraft` — its htmlContent has changed, and
  // leaving the draft on the pre-edit body would arm the next "Save plate" to
  // silently undo this edit. Replacing `template` also re-runs the preview and
  // the cell fetch, which is the required re-read.
  //
  // Returns true only when the server accepted it; EmailCellEditor keeps the
  // editor open with the typed text on anything else.
  const handleSaveCell = async ({ cellId, innerHtml }) => {
    const result = await editEmailTemplateCell({
      templateId: template.templateId,
      cellId,
      innerHtml,
    });

    if (result && result.isAuth && result.success && result.template) {
      setTemplate(result.template);
      const savedDraft = draftFromTemplate(result.template);
      setDraft(savedDraft);
      setOriginalDraft(savedDraft);
      // A cell edit only ever happens off the block path, so this is null on
      // both sides — but `template` has been replaced, and leaving the two out
      // of step is how a stale document survives a save.
      const savedDoc = docFromTemplate(result.template);
      setBlockDoc(savedDoc);
      setOriginalBlockDoc(savedDoc);
      setAttemptedSave(false);
      setCellDirty(false);
      handleStatus('icon-checkbox', 'Cell saved.', 'green');
      return true;
    }

    if (result && result.isAuth) {
      // planCellEdit's own refusal reason — an unknown or moved cell,
      // replacement content that nests a table, a variable nothing provides.
      // It names what went wrong; a generic message would not. Nothing was
      // written and no revision was pushed.
      handleStatus('icon-close-solid', result.error || 'That cell could not be saved.', 'red');
    } else if (result) {
      handleStatus('icon-close-solid', 'You are not authorized to save this template.', 'red');
    } else {
      handleStatus('icon-close-solid', 'Save failed — check your connection and try again.', 'red');
    }

    return false;
  };

  const strip = buildPlateStrip({
    status: platesStatus,
    templates: scopePlates,
    languages,
    currentLanguage: language,
    // The plate being edited is on screen, so it exists whatever the strip
    // fetch did. Deriving its cell from `template` keeps it correct - and keeps
    // its tooltip from reading "No EN plate for this campaign" while you are
    // editing the EN plate.
    currentTemplate: template,
  });
  // null (the check could not run), not [] (checked, nothing to review).
  const needReview = platesNeedingReview(strip);
  const platesFailed = strip.status === PLATE_STRIP_FAILED;
  const retryPlates = () => setPlatesReloadToken(previous => previous + 1);
  // The toolbar's own claim about this plate. "Saved <time>" is a statement of
  // fact about the server, and a failed reload is exactly the case where it is
  // not one - so it is not allowed to be the fallback of a plain `dirty` test.
  let savedChip = <span className="locked">{`Saved ${formatTimestamp(template.updatedAt)}`}</span>;
  if (dirty) {
    savedChip = <span className="locked dirty">Unsaved changes</span>;
  } else if (reloadFailed) {
    savedChip = <span className="locked dirty">Not in sync</span>;
  }
  // What the proof pane is handed. Off the block path it is the server's render
  // of the SAVED template — the only honest thing to show for hand-written HTML
  // nobody generated here. In the builder it is this session's own debounced
  // render of the document on the canvas, which is the same string the save
  // sends.
  //
  // No `note` is passed to the pane any more. It said which of the two renders
  // this was, and the distinction is real, but the header is the most crowded
  // strip on the screen and the label was reading as decoration.
  let proofHtml = preview ? preview.html : '';
  let proofText = preview ? preview.text : draft.textContent;
  let proofSubject = (preview && preview.subject) || draft.subject;
  if (builderOn) {
    proofHtml = blocksProof;
    proofText = draft.textContent;
    proofSubject = draft.subject;
  }
  const usedVariables = extractVariables(
    `${effectiveDraft.subject} ${effectiveDraft.htmlContent} ${effectiveDraft.preheader} ${effectiveDraft.textContent}`,
  );
  const cellPanel = buildCellPanels({
    status: cellsStatus, cells, coverage: cellCoverage,
  });
  // Open unprompted whenever the cells are not the whole email (or we cannot
  // tell); otherwise folded away behind its own button, the way the design
  // folds the Header and Footer panels. Available either way — it is the only
  // way to edit the table chrome.
  const htmlEditorOpen = cellPanel.showHtmlEditor || isHtmlOpen;

  return (
    <div className="emails-app">
      {/*
        TWO DECKS, not one line.

        This header carries eleven controls in six groups, and only three of them
        are used on a normal visit — switch plate, save, go back. Two are never
        clicked at all: the template name and the saved timestamp are STATUS, and
        they were dressed the same as the controls beside them.

        So the row is cut where the meaning changes. Upper deck answers "where am
        I", lower deck "what can I do to it", and the plate strip leads the lower
        deck because switching plates is the thing done most.

        The `Plate` and `Active` labels are gone with it: four language cells and
        the word "sending" already say what they are, and the labels cost a line
        of height each to repeat it.

        Only one way back, to this plate's own preview. "All campaigns" used to sit
        beside it and is gone — the preview screen has that link, so the list is
        still one step further, and two adjacent back-links at the same weight made
        neither of them read as the way out.
      */}
      <div className="toolbar decks">
        <div className="deck">
          <Link
            to={`/cms/emails/${scope}/${language}`}
            className="btn quiet"
            onClick={handleBackClick}
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M8.5 2L3.5 7l5 5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Preview
          </Link>
          <div className="titleblock">
            {/*
              The campaign is the headline, not `name`.

              `name` is a machine id that duplicates templateId — every one in
              this corpus is exactly `<scope>-<language>` — and it was the biggest
              text on the screen. It also drifts: a clone copied it verbatim, so a
              campaign cloned to test_jose_2027 announced itself as
              ps-presalesbcn26-en. That copy is fixed, but the deeper problem was
              giving a redundant, drift-prone field the most prominent position.

              The scope is what identifies this plate and cannot go stale — it is
              the campaign, the Braze flag and the routing key at once. templateId
              stays underneath, quiet, where a technical id belongs.
            */}
            <div className="tname">{template.scope}</div>
            <div className="scope-key tkey">{template.templateId}</div>
          </div>
          <div className="deck-end">{savedChip}</div>
        </div>

        <div className="deck">
          <span className="plates plateswitch">
            {strip.plates.map((plate) => {
              // Not known yet, or the fetch failed: a placeholder that makes no
              // claim either way. Never an empty square - that is a statement
              // that the plate does not exist.
              if (!plate.known) {
                return (
                  <span
                    key={plate.language}
                    className={platesFailed ? 'plate-cell is-unknown is-failed' : 'plate-cell is-unknown'}
                    title={platesFailed
                      ? 'This campaign\u2019s other plates could not be loaded — this is not a statement that they are missing'
                      : 'Still checking which plates this campaign has'}
                  >
                    ·
                  </span>
                );
              }

              if (plate.isCurrent || !plate.exists) {
                return (
                  <span
                    key={plate.language}
                    className={`plate-cell ${plate.state}${plate.isCurrent ? ' is-current' : ''}`}
                    title={plate.isCurrent ? `${plate.language.toUpperCase()} — the plate you are editing` : `No ${plate.language.toUpperCase()} plate for this campaign`}
                  >
                    {plate.language}
                  </span>
                );
              }

              return (
                <Link
                  key={plate.language}
                  to={`/cms/emails/${scope}/${plate.language}/edit`}
                  onClick={handleBackClick}
                  className={`plate-cell ${plate.state} is-dimmed`}
                  title={plate.needsReview
                    ? `${plate.language.toUpperCase()} — copied, not reviewed yet`
                    : `${plate.language.toUpperCase()} — open this plate`}
                >
                  {plate.language}
                </Link>
              );
            })}
            {strip.status === PLATE_STRIP_LOADING && (
              <span className="locked stripnote">Checking plates…</span>
            )}
            {platesFailed && (
              <span className="locked stripnote failed">Plates unavailable</span>
            )}
          </span>

          <span className="swline">
            <span
              className={template.isActive ? 'sw on' : 'sw'}
              role="img"
              aria-label={template.isActive ? 'Active' : 'Inactive'}
            />
            <span className="swtext">{template.isActive ? 'sending' : 'not sending'}</span>
          </span>

          <div className="toolbar-actions">
            <button
              type="button"
              className="btn ghost"
              onClick={() => setIsRevisionsOpen(previous => !previous)}
            >
              Revisions
            </button>
            <button type="button" className="btn ghost" onClick={() => handleModalClick('testSend')}>
              Send test
            </button>
            <button type="button" className="btn ghost" onClick={() => handleModalClick('clone')}>
              Clone
            </button>
            <button type="button" className="btn" onClick={handleSave} disabled={isSaving || reloadFailed}>
              {isSaving ? 'Saving…' : 'Save plate'}
            </button>
          </div>
        </div>
      </div>

      {reloadFailed && (
        <div className="banner bad">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#e3342f" strokeWidth="1.7">
            <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
            <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
          </svg>
          <span>
            <b>This plate could not be re-read, so what is on screen may not be what is stored.</b>
            {' A restore reported success but the reload after it did not land — the subject, '}
            {'preheader and HTML below are still the version from before it. Saving the plate '}
            {'is paused, because it would write this older body back over the restored one. '}
            {'Reload to try again; editing a single cell is unaffected — those are spliced '}
            {'into the saved document on the server, not into what you see here.'}
          </span>
          <span className="right">
            <button type="button" className="btn tiny ghost" onClick={loadTemplate}>Reload</button>
          </span>
        </div>
      )}

      {platesFailed && (
        <div className="banner bad">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#e3342f" strokeWidth="1.7">
            <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
            <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
          </svg>
          <span>
            <b>This campaign&apos;s other plates could not be loaded.</b>
            {' The review check did not run — a plate flagged for review would not be '}
            {'reported here. Editing and saving this plate is unaffected.'}
          </span>
          <span className="right">
            <button type="button" className="btn tiny ghost" onClick={retryPlates}>Retry</button>
          </span>
        </div>
      )}

      {needReview && needReview.length > 0 && (
        <div className="banner">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#8a5a12" strokeWidth="1.7">
            <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
            <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
          </svg>
          <span>
            <b>{`${needReview.map(code => code.toUpperCase()).join(' and ')} still need review`}</b>
            {' — they were copied by a clone and have not been opened since.'}
          </span>
          <span className="right">
            {needReview.map(code => (
              <Link
                key={code}
                to={`/cms/emails/${scope}/${code}/edit`}
                onClick={handleBackClick}
                className="btn tiny ghost"
              >
                {`Open ${code}`}
              </Link>
            ))}
          </span>
        </div>
      )}

      {template.needsReview && (
        <div className="banner">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#8a5a12" strokeWidth="1.7">
            <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
            <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
          </svg>
          <span>
            <b>This plate is flagged for review.</b>
            {' It was copied word for word from another campaign — check the dates, links and copy before it sends.'}
          </span>
        </div>
      )}

      {status.text && <Notification {...status} />}

      <DragDropContext onDragEnd={handleBlockDragEnd}>
        {/* In the builder the right-hand pane is not a proof any more, it is the
            thing being worked on, so the columns swap emphasis: the palette and
            the settings get a fixed rail and the email gets the rest. */}
        <div className={builderOn ? 'press wideproof' : 'press'}>
          <div className="stack">
            <div className="panel">
              <div className="head">
                <h4>Inbox line</h4>
                <div className="right">
                  <span className="locked">{template.language.toUpperCase()}</span>
                </div>
              </div>
              <div className="body">
                <div className="field">
                  <label htmlFor="email-editor-subject">
                    Subject
                    <input
                      id="email-editor-subject"
                      type="text"
                      className="textinput"
                      value={draft.subject}
                      onChange={event => setField('subject', event.target.value)}
                    />
                  </label>
                </div>
                <div className="field">
                  <label htmlFor="email-editor-preheader">
                    Preheader
                    <input
                      id="email-editor-preheader"
                      type="text"
                      className="textinput"
                      value={draft.preheader}
                      onChange={event => setField('preheader', event.target.value)}
                    />
                  </label>
                  <p className="hint">
                    Shown after the subject in the inbox list. Leave it empty and clients fall
                    back to scraping the first line of the body.
                  </p>
                </div>
              </div>
            </div>

            {/* Two things used to sit here and both are gone.

                A banner saying "you are looking at a new, empty block document" —
                untrue once a plate arrives auto-converted with its blocks already
                in it. It also explained that nothing is written until Save plate,
                which was worth saying while converting was a deliberate act with
                its own button, and is just the ordinary behaviour of an editor now
                that a plate simply opens in the builder.

                And the conversion review: the converted blocks shown beside the
                saved email, with a "discard and keep the HTML" way out. That made
                sense when the HTML was somewhere you could go back TO. It is not —
                the HTML editors are behind RENDER_HTML_EDITORS and off, blocks are
                the only way this tab edits an email, so the review offered a
                choice with one real option and put a wall of diff notes in front
                of the thing being edited.

                The needs-review banner above still carries the one warning that is
                specific to this plate, and the refusal below still stops a
                conversion that cannot be made honestly. */}

            {/* THE ONLY THING ABOUT THE EMAIL IN THIS COLUMN. The palette, and
                nothing that draws the email a second time: the email is the pane
                on the right, it is dropped into there, selected there and typed
                into there, and its properties float over it there. */}
            {builderOn && (
              <div className="panel">
                <div className="head">
                  <h4>Blocks</h4>
                  <span className="locked">{`${blockDoc.blocks.length} in this email`}</span>
                </div>
                <div className="body">
                  <BlockPalette onAdd={handleAddBlock} />
                </div>
              </div>
            )}

            {/* The cell panels. Not drawn any more — EmailCellEditor itself
                returns null while RENDER_HTML_EDITORS is false, and the mount is
                gated here too so that the reason is visible from the screen's
                own file rather than only from the component's. */}
            {RENDER_HTML_EDITORS && !builderOn && (
            <EmailCellEditor
              // Keyed on the plate, so the per-cell drafts inside it belong to
              // ONE template. Every save replaces `template` with a new object
              // for the same templateId, which is what keeps drafts across a
              // save; switching plate is a different templateId and remounts,
              // and the plate switcher already asks before leaving unsaved work.
              key={template.templateId}
              panel={cellPanel}
              blocked={documentDirty}
              onSave={handleSaveCell}
              onDirtyChange={setCellDirty}
            />
            )}

            {/* A "Generated HTML" panel used to sit here, reporting the
                character count of the serialised document and explaining that it
                is rewritten on every save. Removed: in the builder the blocks are
                the document, and a character count of the thing they compile to
                is not something anyone edits or acts on. The Full HTML panel
                below is an EDITOR, which is a different question — see
                htmlEditorSurfaces.js for why it is gated rather than deleted. */}
            {RENDER_HTML_EDITORS && !builderOn && (
              <div className="panel">
                <div className="head">
                  <h4>Full HTML</h4>
                  <span className="locked">
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="2.2" y="5.3" width="7.6" height="5.2" rx="1" />
                      <path d="M4 5.3V3.8a2 2 0 014 0v1.5" />
                    </svg>
                    Tables · HTML only
                  </span>
                  {!cellPanel.showHtmlEditor && (
                    <div className="right">
                      <button
                        type="button"
                        className="btn tiny ghost"
                        onClick={() => setIsHtmlOpen(previous => !previous)}
                      >
                        {isHtmlOpen ? 'Hide HTML' : 'Edit HTML'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="body">
                  {htmlEditorOpen ? (
                    <React.Fragment>
                      <HtmlSourceEditor
                        id="email-editor-html"
                        label="HTML source"
                        value={draft.htmlContent}
                        onChange={event => setField('htmlContent', event.target.value)}
                      />
                      <p className="hint">
                        The whole document — head, tables, MSO conditional comments and all.
                        Edited as source on purpose: a rich editor would strip the parts that
                        make these render in Outlook. Changes here are written by
                        <b> Save plate</b>
                        , not by the cell panels above.
                      </p>
                    </React.Fragment>
                  ) : (
                    <p className="htmlfold">
                      {`Complete document · ${draft.htmlContent.length.toLocaleString()} characters · the tables around the copy above`}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/*
              No "build with blocks?" panel any more. Blocks are how this tab
              edits an email, so the choice is not offered — a plate without a
              block document is converted when it opens (see the effect above).

              What remains is the refusal: the specific things this email holds
              that no block can carry, so the answer to "why can I not edit this"
              is on screen rather than in someone's head.
            */}
            {!builderOn && declinedConversion && (
              <div className="panel">
                <div className="head">
                  <h4>Cannot open in the builder</h4>
                </div>
                <div className="body">
                  <div className="banner bad">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#e3342f" strokeWidth="1.7">
                      <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
                      <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
                    </svg>
                    <span>
                      <b>This email could not be opened in the builder, and nothing was changed.</b>
                      {' Rebuilding it as blocks would lose the following, so it was declined '}
                      {'rather than done partially:'}
                      <ul className="convertlist">
                        {conversion.unconverted.map(entry => (
                          <li key={`${entry.where}-${entry.what}`}>
                            <b>{entry.what}</b>
                            {` (${entry.where}) — ${entry.reason}`}
                          </li>
                        ))}
                      </ul>
                      It still sends exactly as it does today — nothing about it has changed.
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="panel">
              <div className="head"><h4>Variables</h4></div>
              <div className="body">
                <div className="chips">
                  {providedVariables.length === 0 && (
                    <span className="chip unused">
                      {providedVariablesLoaded
                        ? 'This campaign provides no variables'
                        : 'Loading what this campaign provides…'}
                    </span>
                  )}
                  {providedVariables.map(variableName => (
                    <span
                      key={variableName}
                      className={usedVariables.includes(variableName) ? 'chip used' : 'chip unused'}
                    >
                      {usedVariables.includes(variableName) && (
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M2 6.3l2.6 2.6L10 3.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                      {variableName}
                      {!usedVariables.includes(variableName) && ' · not used'}
                    </span>
                  ))}
                  {usedVariables
                    .filter(variableName => providedVariablesLoaded
                      && !providedVariables.includes(variableName))
                    .map(variableName => (
                      <span key={variableName} className="chip broken">
                        {`${variableName} · nothing provides this`}
                      </span>
                    ))}
                </div>
                <p className="hint">
                  From this campaign&apos;s sender. Anything else you type renders
                  {' '}
                  <b>empty</b>
                  {' — it is flagged here, and the save is blocked before it can go out.'}
                </p>

                <HtmlSourceEditor
                  id="email-editor-sample-data"
                  label="Sample data (JSON)"
                  value={draft.sampleDataJson}
                  onChange={event => setField('sampleDataJson', event.target.value)}
                  rows={8}
                />
              </div>
            </div>

            {attemptedSave && !validation.valid && (
              <div className="banner bad">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#e3342f" strokeWidth="1.7">
                  <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
                  <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
                </svg>
                <span>
                  <b>Fix these before saving</b>
                  <ul>
                    {validation.errors.map((error, index) => (
                      // The code alone is not unique: a document can have several
                      // invalid blocks, each with its own message.
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={`${error.code}-${error.variable || ''}-${index}`}>{error.message}</li>
                    ))}
                  </ul>
                </span>
              </div>
            )}

            {isRevisionsOpen && (
              <EmailRevisionHistory
                templateId={template.templateId}
                revisions={template.revisions}
                liveSavedAt={template.updatedAt}
                dirty={dirty}
                onRestored={loadTemplate}
              />
            )}
          </div>

          {/* THE EMAIL. In the builder this pane is the editing surface — the
              canvas goes in as children under an "Edit" button that comes first
              on the device control, and the block properties float over it. The
              600px / Mobile / Plain text renders are the same sandboxed iframe
              they have always been and stay one click away, because they are the
              only thing here that shows what the SERVER will send.
 */}
          <EmailProofPane
            title={builderOn ? 'Email' : 'Proof'}
            fromName={template.appName || 'Sender'}
            subject={proofSubject}
            preheader={draft.preheader}
            html={proofHtml}
            text={proofText}
            isLoading={!builderOn && isPreviewLoading}
            error={builderOn ? null : previewError}
            // Only during a review, and it is the SERVER's render of the saved
            // template — the same thing the proof shows off the block path, so
            // the left half of the comparison is the email as it really goes
            // out, not a second local render of it.
            overlay={builderOn ? (
              <BlockPropertiesPanel
                doc={blockDoc}
                selectedId={selectedBlockId}
                onChangeBlock={(id, patch) => setBlockDoc(previous => withBlockProps(previous, id, patch))}
                onChangeSettings={patch => setBlockDoc(previous => withSettings(previous, patch))}
                onSelect={setSelectedBlockId}
              />
            ) : null}
          >
            {builderOn ? (
              <BlockCanvas
                doc={blockDoc}
                selectedId={selectedBlockId}
                onChange={setBlockDoc}
                onSelect={setSelectedBlockId}
                onPatchBlock={(id, patch) => setBlockDoc(previous => withBlockProps(previous, id, patch))}
              />
            ) : null}
          </EmailProofPane>
        </div>
      </DragDropContext>

      {isTestSendModalOn && (
        <Modal title="Send test email" handleClick={handleModalClick} type="testSend">
          <EmailTestSendModal
            // effectiveDraft, not draft: in the builder the html that would be
            // sent is the one generated from the blocks on screen. Sending
            // draft.htmlContent here would test-send the OLD stored email and
            // report it as this document.
            draft={effectiveDraft}
            templateId={template.templateId}
            onClose={() => handleModalClick('testSend')}
          />
        </Modal>
      )}

      {isCloneModalOn && (
        <Modal title="Clone campaign" handleClick={handleModalClick} type="clone">
          <EmailCloneModal
            sourceScope={template.scope}
            sourcePlates={strip.plates}
            sourcePlatesStatus={strip.status}
            onClose={() => handleModalClick('clone')}
          />
        </Modal>
      )}
    </div>
  );
};

EmailTemplateEditor.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      scope: PropTypes.string.isRequired,
      language: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default EmailTemplateEditor;
